import React, { useState } from 'react';
import loadable from '@loadable/component';
import { Col, Row } from '@perxis-auto/ui-kit/lib/components/Grid';

import useCarData from '@/features/cars/hooks/useCarData';
import useCreditData from '@/features/credit/hooks/useCreditData';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import isDefined from '@/utils/isDefined';

import { FinanceDrawerEnum, FinanceProps } from './Finance.types';

import styles from './Finance.module.scss';

const CreditDrawer = loadable(() => import('./components/CreditDrawer'));
const CreditCard = loadable(() => import('./components/CreditCard'));
const TradeInDrawer = loadable(() => import('./components/TradeInDrawer'));
const TradeInCard = loadable(() => import('./components/TradeInCard'));

function Finance({ creditPriceLabel, tradeInPriceLabel }: FinanceProps) {
  const [currentDrawer, setCurrentDrawer] = useState<FinanceDrawerEnum | null>(null);

  const isCreditEnable = useAppSelector(rootSelectors.config.creditEnableSelector);
  const isTradeInEnable = useAppSelector(rootSelectors.config.tradeInEnableSelector);

  const { data: carItem } = useCarData();

  const carId = carItem?.id;
  const model = carItem?.data?.model;
  const price = carItem?.data?.price;

  const creditData = useCreditData(carId);

  if (!carItem) {
    return null;
  }

  const isCreditCardShow =
    isCreditEnable &&
    isDefined(carId) &&
    isDefined(model) &&
    isDefined(price) &&
    isDefined(creditData) &&
    isDefined(creditData.previewProgram);

  const isTradeInCardShow = isTradeInEnable;

  if (!isCreditCardShow && !isTradeInCardShow) {
    return null;
  }

  const handleClick = (drawer: FinanceDrawerEnum) => {
    return () => {
      setCurrentDrawer(drawer);
    };
  };

  const handleClose = () => setCurrentDrawer(null);

  const renderDrawer = (drawer: FinanceDrawerEnum | null) => {
    switch (drawer) {
      case FinanceDrawerEnum.Credit:
        return <CreditDrawer carId={carId!} onClose={handleClose} />;
      case FinanceDrawerEnum.TradeIn:
        return <TradeInDrawer onClose={handleClose} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.finance}>
      <Row gutter={[18, 'vertical-sky']}>
        {isCreditCardShow && (
          <Col xs={24} sm={24} md={isTradeInCardShow ? 12 : 24}>
            <CreditCard
              data={creditData}
              onClick={handleClick(FinanceDrawerEnum.Credit)}
              priceLabel={creditPriceLabel}
            />
          </Col>
        )}
        {isTradeInCardShow && (
          <Col xs={24} sm={24} md={isCreditCardShow ? 12 : 24}>
            <TradeInCard
              onClick={handleClick(FinanceDrawerEnum.TradeIn)}
              priceLabel={tradeInPriceLabel}
            />
          </Col>
        )}
      </Row>
      {renderDrawer(currentDrawer)}
    </div>
  );
}

export default React.memo(Finance);
